import React, { useEffect, useState, useContext } from 'react';
import { connect } from 'react-redux';
import { Breakpoint } from '@material-ui/core/styles/createBreakpoints';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import Typography from '@material-ui/core/Typography';
import { repositories } from 'api';
import { useWidth } from 'common/hooks';
import Carousel from 'common/components/Carousel';
import ExpansionPanel from 'common/components/ExpansionPanel';
import ShippingCost from 'common/components/ShippingCost';
import { StoreBasicDataApiStructure } from 'common/types';
import ChipPanel from '../../components/ChipPanel';
import ProductCover from '../../components/ProductCover';
import Details from '../../components/Details';
import MainDataPanel from '../../components/MainDataPanel';
import { getProductData } from '../../common/productData';
import Set from './components/Set';
import { ProductsSetType } from 'api/repositories';
import { productsSetOperations } from 'store/ducks/productsSet';
import { shopCartOperations } from 'store/ducks/shopCart';
import Button from 'common/components/Button';
import { useForm } from 'react-hook-form';
import { formatPrice } from 'common/functions';
import { NotificationStateContext } from 'common/components/NotificationProvider';
import { makeProductsSetStyles } from 'styles/pages/productsSet';
import { PendingData, } from 'store/ducks/pendingData/types';

type Props = {
    addPromotionalSet: Function;
    addToShopCart: Function;
    fetchProductsSet: Function;
    productsSet: StoreBasicDataApiStructure<ProductsSetType>;
    pendingData: {
        carts: boolean;
        cartPromotionalSet: boolean;
    }
};

type MappingType = {
    productsSet: StoreBasicDataApiStructure<repositories.ProductsSetType>;
    pendingData: PendingData;
};

// TODO PB Move to common.
enum BREAKPOINTS {
    MD = 'md',
    LG = 'lg',
    XL = 'xl',
}

enum IDS {
    DESCRIPTION = 1,
    SHIPPING_COST = 2,
}

const notificationConfig = {
    open: true,
    text: 'Zestaw został dodany do koszyka',
    anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
    },
};

const ProductsSet: React.FC<StoreBasicDataApiStructure<repositories.SimpleProductType> & Props> = ({
    addPromotionalSet,
    data,
    fetchProductsSet,
    productsSet,
    pendingData,
}) => {
    const [productsSetPrice, setProductsSetPrice] = useState(0);
    const {
        control,
        errors,
        formState: { isSubmitted },
        getValues,
        handleSubmit,
        triggerValidation,
    } = useForm();
    const [expandedPanelId, setExpandedPanelId] = useState<number | null>(null);
    const currentWidth = useWidth();

    const { summary } = makeProductsSetStyles();

    const notification = useContext(NotificationStateContext);

    const { code: setCode } = data;

    const setPrice = (fieldName: string) =>
        setTimeout(() => {
            const values = getValues({ nest: true });
            let price = 0;

            values.sections.forEach((section: any) => {
                section.items.forEach((item: any) => {
                    if (item.quantity < 0) return;

                    price += item.quantity * section.price;
                });
            });

            triggerValidation(fieldName);
            setProductsSetPrice(price < 0 ? 0 : price);
        });

    const descriptionPanelExpanded: Array<Breakpoint> = [
        BREAKPOINTS.MD,
        BREAKPOINTS.LG,
        BREAKPOINTS.XL,
    ];

    useEffect(() => {
        setCode && fetchProductsSet(setCode);
    }, [ setCode, fetchProductsSet, ]);

    useEffect(() => {
        descriptionPanelExpanded.includes(currentWidth)
            ? setExpandedPanelId(IDS.DESCRIPTION)
            : setExpandedPanelId(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ currentWidth, ]);

    const handleChange = (panel: number) => (e: React.ChangeEvent<{}>, isExpanded: boolean) =>
        setExpandedPanelId(isExpanded ? panel : null);

    const { author, flipbook, gallery, labels, MEN, thumbnail, title } = getProductData(data);

    const invokeAddToShopCart = () => {
        const showNotification = () => {
            notification.setConfig(notificationConfig);
        };

        const data = getValues({ nest: true });

        addPromotionalSet(
            {
                setCode,
                items: [
                    ...[].concat
                        .apply(
                            [],
                            data.sections.map((section: any) => section.items),
                        )
                        .filter((product: any) => product.quantity > 0),
                ],
            },
            showNotification,
        );
    };

    return (
        <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
                <Hidden smDown>
                    <ProductCover thumbnail={thumbnail} />
                    <Carousel gallery={gallery} flipbook={flipbook} />
                </Hidden>
                <Hidden mdUp>
                    <MainDataPanel author={author} MEN={MEN} title={title} />
                    <ChipPanel data={labels} />
                </Hidden>
            </Grid>
            <Grid item xs={12} md={8}>
                <Hidden mdUp>
                    <ProductCover thumbnail={thumbnail} />
                    <Carousel gallery={gallery} flipbook={flipbook} />
                </Hidden>
                <Grid item xs={12} md={10} lg={8}>
                    <Hidden smDown>
                        <MainDataPanel author={author} MEN={MEN} title={title} />
                    </Hidden>
                </Grid>
                <Grid item xs={12}>
                    <Hidden smDown>
                        <Box alignItems="flex-start" display="flex" justifyContent="space-between">
                            <ChipPanel data={labels} />
                        </Box>
                    </Hidden>
                    <Box marginTop={5}>
                        <ExpansionPanel
                            id={IDS.DESCRIPTION}
                            expandedPanelId={expandedPanelId}
                            handleChange={handleChange}
                            header="Opis"
                        >
                            <Typography
                                dangerouslySetInnerHTML={{
                                    __html: data.description,
                                }}
                                variant="body2"
                            />
                            <Details data={data.attributes} />
                        </ExpansionPanel>
                        <ShippingCost
                            id={IDS.SHIPPING_COST}
                            expandedPanelId={expandedPanelId}
                            handleChange={handleChange}
                        />
                    </Box>
                </Grid>
                <form
                    onSubmit={handleSubmit(() => {
                        invokeAddToShopCart();
                    })}
                >
                    {productsSet.data?.sections.map((productsSection, index) => (
                        <Set
                            key={productsSection.sectionName}
                            control={control}
                            errors={errors}
                            isSubmitted={isSubmitted}
                            productsSection={productsSection}
                            sectionIndex={index}
                            setPrice={setPrice}
                        />
                    ))}
                    <Box alignItems="flex-end" display="flex" flexDirection="column" marginTop={4}>
                        <Box className={summary}>
                            Wartość zestawu: <b>{formatPrice(productsSetPrice)}</b>
                        </Box>
                        <Button
                            disabled={(isSubmitted && errors?.sections?.length > 0) || pendingData.cartPromotionalSet || pendingData.carts}
                            type="submit"
                        >
                            Dodaj do koszyka
                        </Button>
                    </Box>
                </form>
            </Grid>
        </Grid>
    );
};

const mapStateToProps = ({ productsSet, pendingData }: MappingType) => {
    return {
        productsSet,
        pendingData: {
            cartPromotionalSet: Boolean(pendingData.CART_PROMOTIONAL_SET),
            carts: Boolean(pendingData.CARTS_POST),
        },
    };
};

const mapDispatchToProps = {
    addPromotionalSet: shopCartOperations.addPromotionalSet,
    addToShopCart: shopCartOperations.addToShopCart,
    fetchProductsSet: productsSetOperations.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductsSet);
